/**
 * ExerciseDetailScreen — vista pública de un ejercicio del catálogo.
 *
 * URL: `/exercise/{id}` (no auth, no shell).
 * La abre el cliente del entrenador cuando hace click en un ejercicio dentro
 * del link compartido de su plan — pestaña nueva con GIF grande + ejecución.
 *
 * Layout (post-sesión 6): tarjeta centrada en viewport con 2 columnas
 * (hero GIF | info + ejecución), max-width 1080px. En mobile colapsa a 1 col
 * stacked. Reutiliza las clases `.al-ex-detail-*` del picker del editor
 * (mismo styling), envueltas en un wrapper standalone `.al-exd-card`.
 */
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../api/client";
import { getExerciseById, exerciseGifUrl, type Exercise } from "../api/exercises";
import { Icon } from "../lib/Icon";
import { EmptyState } from "../lib/components";

interface ExerciseDetailScreenProps {
  exerciseId: string;
}

type State =
  | { kind: "loading" }
  | { kind: "ok"; data: Exercise }
  | { kind: "error"; status: number; message: string };

export function ExerciseDetailScreen({ exerciseId }: ExerciseDetailScreenProps) {
  const [state, setState] = useState<State>({ kind: "loading" });

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const data = await getExerciseById(exerciseId);
      setState({ kind: "ok", data });
    } catch (e) {
      const status = e instanceof ApiError ? e.status : 500;
      const msg = e instanceof Error ? e.message : "Error inesperado";
      setState({ kind: "error", status, message: msg });
    }
  }, [exerciseId]);

  useEffect(() => {
    load();
    return () => {
      document.title = "AetherLite";
    };
  }, [load]);

  useEffect(() => {
    if (state.kind === "ok") {
      document.title = `${state.data.name} · AetherLite`;
    }
  }, [state]);

  const goBack = () => {
    if (window.history.length > 1) window.history.back();
    else window.close();
  };

  return (
    <div className="al-exd-root">
      <header className="al-exd-hd">
        <button type="button" className="al-exd-back" onClick={goBack} title="Volver">
          <Icon name="arrowL" size={14} /> Volver
        </button>
        <div className="al-exd-brand">AetherLite</div>
      </header>

      <main className="al-exd-main">
        {state.kind === "loading" && (
          <div className="al-exd-card al-exd-card-loading">
            <div className="al-exd-skel-hero" />
            <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 10 }}>
              <div className="al-exd-skel-line" style={{ width: 240, height: 22 }} />
              <div className="al-exd-skel-line" style={{ width: 180, height: 14 }} />
              <div className="al-exd-skel-line" style={{ width: "100%", height: 12, marginTop: 12 }} />
              <div className="al-exd-skel-line" style={{ width: "95%", height: 12 }} />
              <div className="al-exd-skel-line" style={{ width: "88%", height: 12 }} />
            </div>
          </div>
        )}

        {state.kind === "error" && (
          <div className="al-exd-error-wrap">
            <EmptyState
              icon="x"
              title={state.status === 404 ? "Ejercicio no encontrado" : "Algo salió mal"}
              body={
                state.status === 404
                  ? "El ejercicio que buscás no existe o fue removido del catálogo."
                  : "No pudimos cargar el ejercicio. Reintentá en unos segundos."
              }
              action={
                state.status !== 404 ? (
                  <button className="btn btn-primary" onClick={load}>Reintentar</button>
                ) : null
              }
            />
          </div>
        )}

        {state.kind === "ok" && <ExerciseCard ex={state.data} />}
      </main>

      <footer className="al-exd-footer">
        <span className="tiny muted">
          Catálogo de ejercicios · <strong>AetherLite</strong>
        </span>
      </footer>
    </div>
  );
}

/**
 * Renderiza el ejercicio en formato tarjeta: hero GIF a la izquierda, info
 * + ejecución a la derecha con scroll interno si las instrucciones son largas.
 * Reutiliza el mismo styling que el ExerciseDetail del picker (editor).
 */
function ExerciseCard({ ex }: { ex: Exercise }) {
  const gif = exerciseGifUrl(ex);
  const instructions = ex.instructions || ex.description || "";
  return (
    <div className="al-exd-card">
      <div className="al-ex-detail-hero">
        {gif ? (
          <img src={gif} alt={ex.name} loading="eager" />
        ) : (
          <div className="al-exd-hero-fallback">
            <Icon name="hammer" size={56} />
            <div className="tiny" style={{ marginTop: 8 }}>Sin demo disponible</div>
          </div>
        )}
      </div>
      <div className="al-ex-detail-body al-exd-body">
        <div className="al-ex-detail-scroll">
          <div className="al-ex-detail-name">{ex.name}</div>
          <div className="al-ex-detail-meta" style={{ marginTop: 8 }}>
            {ex.muscle && <span className="al-ex-detail-meta-tag">{ex.muscle}</span>}
            {ex.equipment && <span className="al-ex-detail-meta-tag">{ex.equipment}</span>}
            {ex.difficulty && <span className="al-ex-detail-meta-tag">{ex.difficulty}</span>}
            {ex.secondary_muscles && (
              <span className="al-ex-detail-meta-tag" title="Músculos secundarios">
                + {ex.secondary_muscles}
              </span>
            )}
          </div>
          {instructions ? (
            <div>
              <div className="al-ex-detail-section-label">EJECUCIÓN</div>
              <div className="al-ex-detail-instructions">{instructions}</div>
            </div>
          ) : (
            <div className="tiny muted" style={{ fontStyle: "italic" }}>
              Tu entrenador no agregó indicaciones específicas para este ejercicio.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
